/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { FetchResult } from "@apollo/client";
import CommandInputNullifier from "../Commands/CommandInputNullifier";
import CommandInputValidator from "../Commands/CommandInputValidator";
import { useAppCoffeeMugContext } from "../Components/AppCoffeeMugContext";
import IAppCoffeeMugContext from "../Components/AppCoffeeMugContext/IAppCoffeeMugContext";
import { useAppSnackbar } from "../Components/AppSnackbarContext";
import IAppSnackbar from "../Components/AppSnackbarContext/IAppSnackbar";
import GraphQLSimplifier from "../GraphQL/GraphQLSimplifier";
import InputWrapper from "../GraphQL/InputWrapper";

function handleMutationThen<TData>(
    snackbar: IAppSnackbar,
    executionResult: FetchResult<TData>,
    name: string,
    noSnackbar: boolean,
): Promise<TData | undefined> {
    return new Promise<TData | undefined>((resolve) => {
        if (!noSnackbar) {
            snackbar.pushSuccess(name);
        }

        if (executionResult.data) {
            const simplifiedData = GraphQLSimplifier.simplify(executionResult.data);
            resolve(simplifiedData);
        } else {
            resolve(undefined);
        }
    });
}

function handleMutationCatch(appCoffeeMugContext: IAppCoffeeMugContext): Promise<undefined> {
    // Der Fehler wird durch den ErrorLink verarbeitet
    appCoffeeMugContext.hide();
    return Promise.reject(undefined);
}

async function nullifyAndWrapInput<TInput>(commandInputNullifier: CommandInputNullifier, input: TInput, name: string): Promise<InputWrapper<TInput>> {
    const nullified = await commandInputNullifier.nullify(name, input);
    return { input: nullified };
}

export default function useCommandExecutionHandler() {
    const snackbar = useAppSnackbar();
    const coffeeMug = useAppCoffeeMugContext();
    const validator = new CommandInputValidator();
    const nullifier = new CommandInputNullifier();

    async function execute<TData, TInput>(
        name: string,
        input: TInput,
        noSnackbar: boolean,
        execute: (variables: InputWrapper<TInput>) => Promise<FetchResult<TData>>,
    ): Promise<TData | undefined> {
        if (!(await validator.validate(name, input))) {
            return handleMutationCatch(coffeeMug);
        }

        if (!noSnackbar) {
            snackbar.clear();
        }

        const variables = await nullifyAndWrapInput(nullifier, input, name);
        return execute(variables)
            .then((executionResult) => handleMutationThen(snackbar, executionResult, name, noSnackbar))
            .catch(() => handleMutationCatch(coffeeMug));
    }

    return {
        execute
    };
}